import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Check,
  Grip,
  LayoutGrid,
  type LucideIcon,
  Plug,
  ServerCog,
  Settings,
  UsersRound,
  Wallet,
  Warehouse,
} from 'lucide-react';

import type { MenuSection, MenuSectionKey } from '@/types';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';

type MainMenuProps = {
  sections: MenuSection[];
  activeSection?: MenuSection;
};

const SECTION_ICONS: Record<MenuSectionKey, LucideIcon> = {
  all: LayoutGrid,
  cash: Wallet,
  inventory: Warehouse,
  integrations: Plug,
  settings: Settings,
  staff: UsersRound,
  reports: BarChart3,
  system_settings: ServerCog,
};

export const SECTION_DESCRIPTIONS: Record<MenuSectionKey, string> = {
  all: 'Clients, bookings & sales',
  cash: 'Daily cash operations',
  inventory: 'Stock & supplies',
  integrations: 'Payment terminals',
  settings: 'Catalog & company configuration',
  staff: 'Staff & user logs',
  reports: 'Operations journal',
  system_settings: 'Owners & companies',
};

export const MainMenu = ({ sections, activeSection }: MainMenuProps) => {
  const { t } = useTranslation('header');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('Menu')}
          className="text-muted-foreground shrink-0"
        >
          <Grip className="size-7" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        className="bg-card text-foreground border-border w-96 max-w-[calc(100vw-1rem)] rounded-xl p-2 shadow-xl"
      >
        <div className="flex flex-col gap-1">
          {sections.map((section) => {
            const Icon = SECTION_ICONS[section.key] ?? LayoutGrid;
            const isActive = section.key === activeSection?.key;
            const isDisabled = !!section.placeholder || !section.primary_href;

            return (
              <DropdownMenuItem
                key={section.key}
                asChild
                disabled={isDisabled}
                className={cn(
                  'focus:bg-sidebar-accent cursor-pointer gap-3 rounded-lg p-3',
                  isActive && 'bg-sidebar-accent focus:bg-sidebar-accent',
                )}
              >
                <a href={section.primary_href ?? undefined}>
                  <span
                    className={cn(
                      'bg-sidebar flex size-10 shrink-0 items-center justify-center rounded-md',
                      isActive ? 'text-link' : 'text-muted-foreground',
                    )}
                  >
                    <Icon
                      className={cn(
                        'size-5',
                        isActive ? 'text-link' : 'text-muted-foreground',
                      )}
                    />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className={cn(
                        'truncate text-base font-medium',
                        isActive
                          ? 'text-link'
                          : 'text-sidebar-accent-foreground',
                      )}
                    >
                      {t(section.label)}
                    </span>
                    <span className="text-muted-foreground truncate text-sm">
                      {t(SECTION_DESCRIPTIONS[section.key] ?? '')}
                    </span>
                  </span>
                  {isActive && (
                    <Check className="text-sidebar-accent-foreground size-5 shrink-0" />
                  )}
                </a>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
